import React from 'react';

import { Form } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import styled from 'styled-components';

import { FEES_PRICING_TYPES } from 'constants/careTypes';
import { FeeType } from 'types/fees';

import CloseIcon from '../../../common/Icons/CloseIcon';
import DownCaretIcon from '../../../common/Icons/DownCaretIcon';
import Select from '../../lib/Select';
import {
  FEES_LEVELS_FIELDS_KEYS,
  FIELD_CATEGORY,
  FIELD_LABELS,
  LEVEL_FEES_FIELDS,
} from '../constants';

const StyledCloseIcon = styled.div`
  display: flex;
  visibility: hidden;
  border-radius: 50%;
  cursor: pointer;
  align-items: center;
  margin-top: 9px;
`;

const Container = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  &:hover {
    ${StyledCloseIcon} {
      visibility: visible;
    }
  }
`;

const Label = styled.div`
  display: flex;
  margin-right: 5px;
  margin-top: 9px;
  min-width: 200px;

  justify-content: end;
  font-family: var(--font-bold);
  font-size: 14px;
`;

const StyledMenu = styled.div`
  .ant-select-item {
    font-size: 14px;
    padding: 6px 10px;
    border-bottom: 1px solid var(--line-gray);
  }
`;

const StyledFormItem = styled(Form.Item)`
  width: 220px;

  .ant-form-item-explain-error {
    line-height: 16px;
    white-space: pre-line;
  }
`;

interface Props {
  category: string;
  field: string;
  options: DefaultOptionType[];
  name?: (string | number)[];
  onRemove?: (field: string) => void;
  dataTestId: string;
}

const ShopDataModalFeesEntryDropdown = ({ category, field, options, onRemove, name, dataTestId }: Props) => {
  const enabledRemoval = typeof onRemove === 'function';
  const [, type] = name ?? [];
  const hasPositiveValue = (value: unknown) => Number(value) > 0;
  const hasText = (value: unknown) => String(value ?? '').trim().length > 0;
  const feeFieldPath = (type: string | number, field: string) => [FIELD_CATEGORY.FEES, type, field];
  const validationDependencies =
    type === FeeType.CARE_FEE
      ? [
          feeFieldPath(type, LEVEL_FEES_FIELDS.AVERAGE_COST),
          feeFieldPath(type, LEVEL_FEES_FIELDS.FEE_NOTE),
          ...FEES_LEVELS_FIELDS_KEYS.map((levelField: string) => feeFieldPath(type, levelField)),
        ]
      : undefined;

  return (
    <Container>
      <Label>{FIELD_LABELS[category][field]}</Label>
      <StyledFormItem name={name ?? [category, field]} dependencies={validationDependencies} rules={[
        ({ getFieldValue }) => ({
          validator() {
            const [, type] = name ?? [];

            if (type !== FeeType.CARE_FEE) {
              return Promise.resolve();
            }

            const pricingType = getFieldValue(feeFieldPath(type, LEVEL_FEES_FIELDS.FEES_PRICING_TYPE));
            const populatedFields = getFieldValue([FIELD_CATEGORY.FEES, type, 'fields']) ?? [];
            const hasLevelInput = FEES_LEVELS_FIELDS_KEYS.some((levelField: string) =>
              populatedFields.includes(levelField)
            );
            const hasPositiveLevel = FEES_LEVELS_FIELDS_KEYS.some((levelField: string) =>
              hasPositiveValue(getFieldValue(feeFieldPath(type, levelField)))
            );
            const hasAverageCareWithNote =
              hasPositiveValue(getFieldValue(feeFieldPath(type, LEVEL_FEES_FIELDS.AVERAGE_COST))) &&
              hasText(getFieldValue(feeFieldPath(type, LEVEL_FEES_FIELDS.FEE_NOTE)));

            return pricingType === FEES_PRICING_TYPES.LEVELS &&
              !hasLevelInput &&
              !hasPositiveLevel &&
              !hasAverageCareWithNote
              ? Promise.reject(new Error("Add at least one Level, or Average Care with Care Note."))
              : Promise.resolve();
          },
        }),
      ]}>
        <Select
          className="rectangular"
          style={{ width: 220, height: 36 }}
          options={options}
          suffixIcon={<DownCaretIcon />}
          dropdownRender={(menu) => <StyledMenu>{menu}</StyledMenu>}
          dropdownStyle={{
            padding: 0,
            borderRadius: 0,
            border: '1px solid var(--line-gray)',
            borderBottom: 0,
            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
          }}
          data-test-id={dataTestId}
        />
      </StyledFormItem>
      {enabledRemoval && (
        <StyledCloseIcon onClick={() => onRemove(field)}>
          <CloseIcon size={20} />
        </StyledCloseIcon>
      )}
    </Container>
  );
};

export default ShopDataModalFeesEntryDropdown;
